import React, { useState } from 'react';
import { ChevronLeft, TrendingUp, Filter } from 'lucide-react';
import Phase1Dashboard from './Phase1Dashboard';
import Phase2Dashboard from './Phase2Dashboard';
import Phase3Dashboard from './Phase3Dashboard';
import Phase4Dashboard from './Phase4Dashboard';
import Phase5Dashboard from './Phase5Dashboard';

function AdminStatistics({ userId, setActiveComponent }) {
  const [selectedPeriod, setSelectedPeriod] = useState('7days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [activeTab, setActiveTab] = useState('phase1');

  function handlePeriodChange(period) {
    setSelectedPeriod(period);
    // Clear custom dates when switching away from custom
    if (period !== 'custom') {
      setCustomStartDate('');
      setCustomEndDate('');
    }
  }

  const periods = [
    { value: 'today', label: 'Hoy' },
    { value: '7days', label: '7 Días' },
    { value: '30days', label: '30 Días' },
    { value: 'custom', label: 'Personalizado' }
  ];

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4">
            {/* Top Row */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setActiveComponent('OptionsAdmin')}
                className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="text-sm sm:text-base">Volver</span>
              </button>
              <h1 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat flex items-center gap-2">
                <TrendingUp className="w-6 h-6" />
                Estadísticas
              </h1>
              <div className="w-20"></div>
            </div>

            {/* Period Filter */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-semibold text-slate-700 font-montserrat">
                  Período:
                </span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {periods.map(period => (
                  <button
                    key={period.value}
                    onClick={() => handlePeriodChange(period.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold font-montserrat transition-all duration-200 ${
                      selectedPeriod === period.value
                        ? 'bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white shadow-md'
                        : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-[#B1336E]'
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>

              {selectedPeriod === 'custom' && (
                <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center w-full sm:w-auto">
                  <div className="flex gap-2 items-center w-full sm:w-auto">
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      max={customEndDate || undefined}
                      className="px-3 py-2 border-2 border-slate-300 rounded-lg text-sm font-montserrat focus:outline-none focus:border-[#B1336E] flex-1 sm:flex-initial"
                      placeholder="Fecha inicio"
                    />
                    <span className="text-slate-600 font-montserrat">-</span>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      min={customStartDate || undefined}
                      className="px-3 py-2 border-2 border-slate-300 rounded-lg text-sm font-montserrat focus:outline-none focus:border-[#B1336E] flex-1 sm:flex-initial"
                      placeholder="Fecha fin"
                    />
                  </div>
                  {customStartDate && customEndDate && (
                    <span className="text-xs text-slate-600 font-montserrat">
                      {Math.ceil((new Date(customEndDate) - new Date(customStartDate)) / (1000 * 60 * 60 * 24)) + 1} días
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-200 -mb-px overflow-x-auto">
              <button
                onClick={() => setActiveTab('phase1')}
                className={`px-4 py-2 text-sm font-semibold font-montserrat transition-all duration-200 border-b-2 whitespace-nowrap ${
                  activeTab === 'phase1'
                    ? 'border-[#B1336E] text-[#B1336E]'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                General
              </button>
              <button
                onClick={() => setActiveTab('phase2')}
                className={`px-4 py-2 text-sm font-semibold font-montserrat transition-all duration-200 border-b-2 whitespace-nowrap ${
                  activeTab === 'phase2'
                    ? 'border-[#B1336E] text-[#B1336E]'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                Análisis Avanzado
              </button>
              <button
                onClick={() => setActiveTab('phase3')}
                className={`px-4 py-2 text-sm font-semibold font-montserrat transition-all duration-200 border-b-2 whitespace-nowrap ${
                  activeTab === 'phase3'
                    ? 'border-[#B1336E] text-[#B1336E]'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                Operaciones
              </button>
              <button
                onClick={() => setActiveTab('phase4')}
                className={`px-4 py-2 text-sm font-semibold font-montserrat transition-all duration-200 border-b-2 whitespace-nowrap ${
                  activeTab === 'phase4'
                    ? 'border-[#B1336E] text-[#B1336E]'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                Entregas Provincia
              </button>
              <button
                onClick={() => setActiveTab('phase5')}
                className={`px-4 py-2 text-sm font-semibold font-montserrat transition-all duration-200 border-b-2 whitespace-nowrap ${
                  activeTab === 'phase5'
                    ? 'border-[#B1336E] text-[#B1336E]'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                Ventas Dropship
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          {activeTab === 'phase1' && (
            <Phase1Dashboard
              userId={userId}
              period={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
            />
          )}
          {activeTab === 'phase2' && (
            <Phase2Dashboard
              userId={userId}
              period={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
            />
          )}
          {activeTab === 'phase3' && (
            <Phase3Dashboard
              userId={userId}
              period={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
            />
          )}
          {activeTab === 'phase4' && (
            <Phase4Dashboard
              userId={userId}
              period={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
            />
          )}
          {activeTab === 'phase5' && (
            <Phase5Dashboard
              userId={userId}
              period={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminStatistics;